import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import {
  Card,
  CardBody,
  Container,
  Row,
} from 'reactstrap';

import { filesize } from 'filesize';
import { uniqueId } from 'lodash';

import { AppContext } from '../../contexts/app';
import api from '../../services/api';
import FileList from '../DragDrop/FileList';
import Upload from '../DragDrop/Upload';
import Notification from '../Notification';

export default function UploadFiles({
  url,
  accept,
  label,
  height,
  parentId,
  initialFiles = [],
  onCountFiles,
}) {
  const notificationRef = useRef();
  const { setLoading } = useContext(AppContext);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  useEffect(() => {
    if (initialFiles.length) {
      setUploadedFiles(
        initialFiles.map((file) => ({
          name: file.name,
          readableSize: filesize(file.size),
          preview: file.file_url,
          progress: 100,
          uploaded: true,
          error: false,
          url: file.file_url,
          id: file._id,
          ...file,
        })),
      );
    }
  }, [initialFiles.length]);

  const updateFile = (fileId, data) => {
    setUploadedFiles(
      (prevState) => {
        if (onCountFiles) {
          onCountFiles(prevState.filter((uploadedFile) => uploadedFile.error === false).length);
        }
        return prevState.map(
          (uploadedFile) => (
            fileId === uploadedFile.id
              ? { ...uploadedFile, ...data }
              : uploadedFile
          ),
        );
      },
    );
  };

  const processUpload = (uploadFile) => {
    const data = new FormData();
    data.append('file', uploadFile.file, uploadFile.name);

    if (parentId) {
      data.append('parentId', parentId);
    }

    api
      .post(url, data, {
        onUploadProgress: (e) => {
          const progress = parseInt(Math.round((e.loaded * 100) / e.total), 10);
          console.log('progress upload', progress);
          updateFile(uploadFile.id, { progress });
        },
      })
      .then((response) => {
        const { data: fileData } = response;
        updateFile(uploadFile.id, {
          uploaded: true,
          id: fileData._id,
          name: fileData.name,
          url: fileData.file_url,
          readableSize: filesize(fileData.size),
          ...fileData,
        });
      })
      .catch(() => {
        updateFile(uploadFile.id, { error: true });
      });
  };

  const handleUpload = (files = []) => {
    const newFiles = files.map((file) => ({
      file,
      id: uniqueId(),
      name: file.name,
      readableSize: filesize(file.size),
      preview: URL.createObjectURL(file),
      progress: 0,
      uploaded: false,
      error: false,
      url: null,
    }));

    setUploadedFiles([...uploadedFiles, ...newFiles]);

    newFiles.forEach((file) => processUpload(file));
  };

  const handleOnDelete = useCallback((id) => {
    (async () => {
      try {
        setLoading(true);
        let urlStr = `${url}/${id}`;
        if (parentId) {
          urlStr = `${urlStr}?parentId=${parentId}`;
        }
        await api.delete(urlStr);
        setUploadedFiles((fs) => {
          const returnData = fs.filter((value) => value.id !== id);
          if (onCountFiles) {
            onCountFiles(returnData.filter((uploadedFile) => uploadedFile.error === false).length);
          }
          return returnData;
        });
        notificationRef.current.notify({
          message: 'Arquivo excluido com sucesso',
        });
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.log(error);
        notificationRef.current.notify({
          message: 'Não foi possível excluir a Arquivo',
          color: 'danger',
        });
      }
    })();
  }, [parentId]);

  const handleDownloadFile = useCallback((filename, name) => {
    (async () => {
      try {
        const res = await api.get(`/download/${filename}/files`, { responseType: 'blob' });
        const fileURL = window.URL.createObjectURL(res.data);
        const alink = document.createElement('a');
        alink.href = fileURL;
        alink.target = '_blank';
        alink.rel = 'noopener noreferrer';
        alink.download = name;
        alink.click();
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);

  return (
    <Container>
      <Notification ref={notificationRef} />
      <Row>
        <Card className="w-100">
          <CardBody>
            <Upload
              accept={accept}
              message={label}
              height={height}
              onUpload={handleUpload}
            />
            <FileList
              files={uploadedFiles}
              onDelete={handleOnDelete}
              onDownload={handleDownloadFile}
            />
          </CardBody>
        </Card>
      </Row>
    </Container>
  );
}
